import { Popover, PopoverContent, PopoverTrigger } from '@nextui-org/react'
import { createTwoFilesPatch } from 'diff'
import { Download } from 'lucide-react'
import { useState } from 'react'
import { Button } from '~/components/common/Button'
import { PopoverNavDownIcon } from '~/components/icons'
import { usePluginWorkspaceSafe } from '~/contexts/PluginWorkspaceContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { type Diff } from '~/protos/agents'
import { bridge } from '~/services/bridge'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { BetterToast } from '~/utils/toast'

interface CodeDownloadOptionsProps {
  diffs: Diff[] | null
  taskId?: number
  workspaceUuid?: string
  conversationUuid?: string
  tupleIndex?: number
  onDownloadOriginalRepo?: () => void
  triggerVariant?: 'default' | 'icon'
}

function generateCombinedPatch(diffs: Diff[]) {
  return (
    diffs
      .map(diff =>
        createTwoFilesPatch(
          diff.aFilePath || '/dev/null',
          diff.bFilePath || '/dev/null',
          (diff.aFileContent || '').trimEnd(),
          (diff.bFileContent || '').trimEnd()
        )
      )
      .join('\n')
      .trimEnd() + '\n'
  )
}

/**
 * Try native save dialog first; if the plugin shell is too old, fall back to writing
 * into a "Downloaded" folder inside the workspace.
 * Returns the display path on success, or null if user cancelled.
 */
async function pluginSaveFile(base64Content: string, filename: string): Promise<string | null> {
  const result = await bridge.saveFile(base64Content, filename)
  if (result.success) return result.data?.path || filename
  if (result.error === 'Save cancelled by user') return null

  if (result.error === 'unsupported') {
    const workspaceInfo = await bridge.getWorkspaceInfo()
    const rootPath = workspaceInfo.data?.rootPath
    if (!rootPath) throw new Error('No workspace path available for download')

    const downloadDir = `${rootPath}/Downloaded`
    await bridge.call('fs', 'mkdir', { path: downloadDir })
    const filePath = `${downloadDir}/${filename}`
    const writeResult = await bridge.call('fs', 'writeBinary', {
      path: filePath,
      content: base64Content
    })
    if (!writeResult.success) throw new Error(writeResult.error || 'Failed to save file')
    return `Downloaded/${filename}`
  }

  throw new Error(result.error || 'Failed to save file')
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  const chunkSize = 0x8000
  let binary = ''
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize))
  }
  return btoa(binary)
}

export default function CodeDownloadOptions({
  diffs,
  taskId = 0,
  workspaceUuid,
  conversationUuid,
  tupleIndex,
  onDownloadOriginalRepo,
  triggerVariant = 'default'
}: CodeDownloadOptionsProps) {
  const [isOpen, setIsOpen] = useState(false)
  const pluginWs = usePluginWorkspaceSafe()
  const { currentWorkspace } = useWorkspaceContext()
  const isInPlugin = pluginWs !== null
  const resolvedWorkspaceUuid = workspaceUuid || currentWorkspace?.uuid || ''

  const handleDownloadDiffPatch = async () => {
    if (!diffs) {
      BetterToast.error('No diffs to download')
      return
    }

    try {
      const patch = generateCombinedPatch(diffs)
      const now = new Date().toISOString().replace(/[:.]/g, '-')
      const [date, timeWithZone] = now.split('T')
      const timestamp = `${date}_${timeWithZone.split('-')[0]}`
      const taskIdForFilename = taskId || 'unknown-task'
      const filename = `curie-diffs-${taskIdForFilename}-${timestamp}.patch`

      if (isInPlugin) {
        const saved = await pluginSaveFile(btoa(patch), filename)
        if (saved !== null) BetterToast.success(`Saved to ${saved}`)
        return
      }

      const blob = new Blob([patch], { type: 'text/plain' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error creating patch file:', error)
      BetterToast.error('Failed to create patch file')
    }
  }

  const handleDownloadZip = async () => {
    if (!taskId) {
      BetterToast.error('Task ID not available')
      return
    }

    try {
      BetterToast.info('Building generated code archive, this may take a moment...')

      const downloadEndpoint =
        conversationUuid && resolvedWorkspaceUuid
          ? `/api/workspace/${resolvedWorkspaceUuid}/conversation/${conversationUuid}/task/${taskId}/download-patched-repo`
          : `/api/task/${taskId}/download-patched-repo`
      const downloadEndpointWithSnapshot =
        tupleIndex !== undefined
          ? `${downloadEndpoint}?${new URLSearchParams({ tuple_index: String(tupleIndex) })}`
          : downloadEndpoint

      const response = await fetchWrapper(downloadEndpointWithSnapshot, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error?.detail || 'Failed to build generated code archive')
      }

      const { url, filename: resolvedFilename } = await response.json()
      const filename = resolvedFilename || `generated-code-task-${taskId}.zip`

      if (isInPlugin) {
        const downloadResponse = await fetch(url)
        if (!downloadResponse.ok) throw new Error('Failed to download generated code archive')
        const base64 = arrayBufferToBase64(await downloadResponse.arrayBuffer())
        const saved = await pluginSaveFile(base64, filename)
        if (saved !== null) BetterToast.success(`Saved to ${saved}`)
        return
      }

      const downloadResponse = await fetch(url)
      if (!downloadResponse.ok) throw new Error('Failed to download generated code archive')
      const blob = new Blob([await downloadResponse.arrayBuffer()], { type: 'application/zip' })
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)

      BetterToast.success('Generated code downloaded successfully!')
    } catch (error) {
      console.error('Error downloading generated code:', error)
      BetterToast.error(
        error instanceof Error ? error.message : 'Failed to download generated code'
      )
    }
  }

  return (
    <Popover
      placement='bottom-end'
      radius='lg'
      offset={4}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      style={{
        zIndex: 1000000 // Note that the z-index of the BaseModal is 999999 (6 * 9)
      }}
      classNames={{
        base: 'min-w-[280px] text-tokens-neutral-text-primary',
        content:
          'p-0 rounded-2xl shadow-popover border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white',
        trigger: 'group'
      }}
    >
      <PopoverTrigger>
        {triggerVariant === 'icon' ? (
          <Button
            variant='borderless'
            className='h-9 w-9 px-0 text-tokens-neutral-icon-hover'
            aria-label='Download options'
            title='Download options'
          >
            <Download className='size-4' />
          </Button>
        ) : (
          <Button
            variant='outlined'
            className='flex items-center gap-1'
            rightIcon={<PopoverNavDownIcon />}
          >
            Download Options
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent>
        <div className='flex w-full flex-col py-2'>
          <button
            className='flex w-full items-center justify-between border-b border-tokens-neutral-border-2 px-4 py-3 text-left hover:bg-tokens-neutral-fill-fill-4'
            onClick={handleDownloadZip}
          >
            <span className='text-base'>Download Generated Code</span>
          </button>
          <button
            className={`flex w-full items-center justify-between px-4 py-3 text-left hover:bg-tokens-neutral-fill-fill-4 ${onDownloadOriginalRepo ? 'border-b border-tokens-neutral-border-2' : ''}`}
            onClick={handleDownloadDiffPatch}
          >
            <span className='text-base'>Download Diff Patch</span>
          </button>
          {onDownloadOriginalRepo && (
            <button
              className='flex w-full items-center justify-between px-4 py-3 text-left hover:bg-tokens-neutral-fill-fill-4'
              onClick={onDownloadOriginalRepo}
            >
              <span className='text-base'>Download Uploaded Repo</span>
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
